import React, { useContext, ChangeEvent, useEffect } from "react";
import Big from "big.js";
import { connect } from "react-redux";
import { AxiosError, AxiosResponse } from "axios";

import PurchaseSummary from "../PurchaseSummary/PurchaseSummary";
import StepsHeader from "@market/components/StepsHeader/StepsHeader";
import Notice from "@appComponents/Notice/Notice";
import Loader from "@appComponents/Loader/Loader";
import StepButtons from "@appComponents/StepButtons/StepButtons";
import Confirmation from "@appComponents/Confirmation/Confirmation";
import PlainCounter from "@appComponents/components/PlainCounter/PlainCounter";
import InfoBox from "@appComponents/InfoBox/InfoBox";
import WalletToPurchaseSelect from "../WalletToPurchaseSelect/WalletToPurchaseSelect";
import { PurchaseAmount } from "../PurchaseAmount/PurchaseAmount";
import { MembershipStatus } from "../MembershipStatus/MembershipStatus";

import FundsService from "@services/Funds.service";

import { usePurchaseForm } from "./usePurchaseForm";
import { animateScrollTo } from "@features/utils/functions";
import { FundsAppContext } from "@features/context";
import { TReducers } from "@features/reducers";
import getPurchaseFees from "./getPurchaseFees";
import {
  getPurchaseExchangeRates,
  updateWalletBalanceAfterTransaction,
} from "@funds/features/action.funds";

import { TUpdateWalletData } from "@funds/interfaces/IFundsGeneral";
import { IWalletDetails } from "@market/interfaces/IWallet";
import IPurchaseForm, { IPurchaseTokensData } from "./IPurchaseForm";
import { addInOutItem } from "@funds/components/InOutHistory/InOutHistory";
import IMembershipStatus, {
  EMembershipStatusRender,
} from "../MembershipStatus/IMembershipStatus";
import { ETransactionType } from "@funds/enums/ETransactionType";
import { EMembership } from "@enums/EMembership";
import ConfirmForm, {
  useConfirmForm,
} from "@appComponents/ConfirmForm/ConfirmForm";

import * as style from "./PurchaseFormStyles.module.scss";

const stepsHeader = ["Select Amount", "Summary", "Confirmation"];
const LAST_PURCHASE_STEP = 3;

export const PurchaseForm: React.FunctionComponent<IPurchaseForm> = (props) => {
  const { appLink } = useContext(FundsAppContext);
  const { state, dispatch } = usePurchaseForm(props);
  const { confirmFormState, setConfirmFormState } = useConfirmForm();
  const purchaseFees = getPurchaseFees(
    state.amount || 0,
    props.purchaseExchangeRates,
    state.currencyToPay,
    props.purchaseFee
  );

  useEffect(() => {
    props.getPurchaseExchangeRates();
  }, [state.exchangeRateChangeTime]);

  const nextStep = (): void => {
    if (!state.nextStepEnable) return;

    if (isOnFinalStep(state.step, LAST_PURCHASE_STEP - 1)) {
      setConfirmFormState(false, true);
      return;
    }

    dispatch({ type: "moveToStep", payload: state.step + 1 });
    animateScrollTo(undefined, document.getElementById("purchaseForm"), 20);
  };

  const purchaseTokens = () => {
    if (state.amount && state.currencyToPay) {
      const data: IPurchaseTokensData = {
        amount: state.amount,
        currency: props.currency,
        payment_currency: state.currencyToPay,
        exchange_rate:
          props.purchaseExchangeRates[`bft_` + state.currencyToPay],
        total_amount_to_pay: purchaseFees.totalToPay,
      };

      setConfirmFormState(true, false);
      dispatch({ type: "handleSubmit", payload: data.exchange_rate });

      FundsService.purchaseToken(data)
        .then((res: AxiosResponse) => {
          setConfirmFormState(false, false);
          dispatch({ type: "moveToStep", payload: state.step + 1 });
          props.updateWalletAfterTransaction({
            type: ETransactionType.purchase,
            amount: process.env.ENABLE_BINANCE_TRADING
              ? res.data.income_amount
              : res.data.amount,
            currency: props.currency,
          });
          addInOutItem!(res.data);
        })
        .catch((err: AxiosError<any>) => {
          setConfirmFormState(false, false);
          if (
            err.response &&
            Object.keys(err.response.data.errors).includes("exchange_rate")
          ) {
            props.getPurchaseExchangeRates().then(() => {
              dispatch({
                type: "setMessage",
                payload:
                  "The exchange rate has been changed. Please go back and readjust your input.",
              });
            });

            return;
          }

          dispatch({
            type: "setMessage",
            payload: "Something went wrong, please try again in few minutes.",
          });
        });
    }
  };

  const prevStep = (): void => {
    if (state.step === 1) return;

    if (state.step > 1) {
      dispatch({ type: "handleBackButton" });
    }

    dispatch({ type: "moveToStep", payload: 1 });

    animateScrollTo(undefined, document.getElementById("purchaseForm"), 20);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement> | string): void => {
    if (typeof e === "string") {
      dispatch({ type: "updateCurrencyToPay", payload: e });
    } else {
      updateAmount(e.target.value);
    }
  };

  const updateAmount = (value: string): void => {
    dispatch({ type: "updateAmount", payload: value });
  };

  const purchaseAll = () => {
    dispatch({ type: "purchaseAll" });
  };

  const actOnTimerEnd = () => {
    setTimeout(() => {
      dispatch({ type: "handleTimerEnd" });
    }, 0);
  };

  const renderPurchaseAmount = () => {
    const isBFTPurchase =
      props.purchaseWallet && props.purchaseWallet.currency === "bft";

    const membershipStatusProps: IMembershipStatus = {
      amount: Big(state.amount || 0).plus(props.purchaseWallet.balance),
      currentStatus: props.membershipStatus,
      onMembershipSelect: updateAmount,
      membershipTresholds: props.membershipTresholds,
      renderType: EMembershipStatusRender.list,
      walletBalance: Big(props.purchaseWallet.balance).round(0, 0),
    };

    return (
      <>
        <Notice noDivider={true}>
          BFT can only be purchased with your USDT/BTC/ETH account balance.
        </Notice>

        <WalletToPurchaseSelect
          availableToPayWallets={props.availableToPayWallets}
          handleUpdate={onChange}
          currencyToPay={state.currencyToPay}
          currency={props.currency}
          priceFor1Token={purchaseFees.priceFor1Token}
          label="To get BFT I want to trade with:"
        />

        {isBFTPurchase && <MembershipStatus {...membershipStatusProps} />}

        <PurchaseAmount
          currency={props.currency}
          purchaseWallet={props.purchaseWallet!}
          purchaseFees={purchaseFees}
          handleUpdate={onChange}
          currencyToPay={state.currencyToPay}
          amount={state.amount || 0}
          purchaseAll={purchaseAll}
          errors={state.errors}
        />

        {isBFTPurchase &&
          props.membershipStatus !== EMembership.premium_plus && (
            <MembershipStatus
              {...membershipStatusProps}
              renderType={EMembershipStatusRender.inline}
            />
          )}
      </>
    );
  };

  const renderSummary = () => {
    const exchangeRatesHaveChanged =
      state.sentExchangeRate !==
      props.purchaseExchangeRates[`bft_` + state.currencyToPay];

    return (
      <>
        {state.message &&
          (exchangeRatesHaveChanged ? (
            <Notice noDivider={true} children={<p>{state.message}</p>} />
          ) : (
            <InfoBox error={true} children={<p>{state.message}</p>} />
          ))}
        {!state.message && !!state.sentExchangeRate ? (
          <Loader moreSpace={true} />
        ) : (
          <PurchaseSummary
            amount={state.amount || 0}
            currencyToPay={state.currencyToPay}
            currency={props.currency}
            purchaseFees={purchaseFees}
            purchaseWallet={props.purchaseWallet}
          />
        )}
      </>
    );
  };

  const renderSteps = () => {
    switch (state.step) {
      case 1:
      default:
        return renderPurchaseAmount();
      case 2:
        return renderSummary();
      case LAST_PURCHASE_STEP:
        return (
          <Confirmation
            data={{
              title: "Purchase Completed",
              texts: [
                "Congratulations. Purchase transfer has been completed and you can find your new funds in your wallet.",
              ],
              link1: {
                text: "Back to Funds",
                url: appLink,
              },
            }}
          />
        );
    }
  };

  const isOnFinalStep = (current: number, final: number): boolean =>
    current === final;

  return (
    <div id="purchaseForm">
      <StepsHeader steps={stepsHeader} current={state.step} />
      {state.exchangeRateChangeTime && (
        <PlainCounter
          completedText={"0 sec"}
          onComplete={actOnTimerEnd}
          timeLeft={state.exchangeRateChangeTime.toString()}
          abbreviated={true}
          beforeText={"Exchange rate updates in: "}
          addedClassName={style.counter}
          key={state.exchangeRateChangeTime.toString()}
        />
      )}
      {props.purchaseExchangeRates ? renderSteps() : <Loader />}
      {state.step < 3 && (
        <StepButtons
          nextStep={nextStep}
          prevStep={prevStep}
          isFirstStep={state.step === 1}
          nextStepEnable={state.nextStepEnable && !confirmFormState.isCalling}
          nextHidden={!!state.sentExchangeRate}
          nextText={
            isOnFinalStep(state.step, LAST_PURCHASE_STEP - 1)
              ? "Confirm & Purchase"
              : "Next"
          }
        />
      )}
      <ConfirmForm
        closeLightbox={() => setConfirmFormState(false, false)}
        actionFunction={purchaseTokens}
        values={{
          headline: "Are you sure you wish to make a purchase?",
          paragraph: `Please note that this operation is irreversible.`,
          actionButton: "Confirm",
        }}
        isVisible={confirmFormState.isDisplayed}
      />
    </div>
  );
};

const mapStateToProps = (store: TReducers) => ({
  purchaseFee: store.appSettings.purchase_token_fee,
  availableToPayWallets: (store.fundsReducer.supportedUserWallets || []).filter(
    (el: IWalletDetails) => availablePayCurrenciesFilter(el, store)
  ),
  membershipStatus: store.userInfo.membership,
  purchaseExchangeRates: store.fundsReducer.purchaseExchangeRates,
  minPurchaseUsdValue: store.appSettings.min_purchase_usd_value,
  membershipTresholds: store.appSettings.membership_thresholds,
});

const mapDispatchToProps = (dispatch: Function) => ({
  getPurchaseExchangeRates: () => dispatch(getPurchaseExchangeRates()),
  updateWalletAfterTransaction: (data: TUpdateWalletData) => {
    dispatch(updateWalletBalanceAfterTransaction(data));
  },
});

const availablePayCurrenciesFilter = (el: IWalletDetails, store: TReducers) =>
  store.appSettings.purchase_token_available_pay_currencies.includes(
    el.currency
  );

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseForm);
